// src/components/user/file_management/services/downloadFolder.js

import JSZip from "jszip";
import { ref, listAll, getMetadata, getDownloadURL } from "firebase/storage";
import { storage } from "../../../../firebase";

/**
 * Recursively list files and subfolders in Firebase Storage path.
 * @param {string} path - Storage path to list.
 * @returns {Promise<Object>} - Folder object with subfolders and files.
 */
async function recursiveListStorage(path) {
  const folder = {
    folderName: path.split("/").pop() || "folder",
    files: [],
    subfolders: [],
  };

  try {
    const folderRef = ref(storage, path);
    const listResult = await listAll(folderRef);

    // List files
    for (const itemRef of listResult.items) {
      try {
        const metadata = await getMetadata(itemRef);
        const downloadURL = await getDownloadURL(itemRef);
        folder.files.push({
          name: itemRef.name,
          size: metadata.size,
          type: metadata.contentType,
          downloadURL,
          storagePath: itemRef.fullPath,
        });
      } catch (error) {
        console.error(
          "Error getting metadata or download URL for file:",
          itemRef.fullPath,
          error
        );
      }
    }

    // Recursively list subfolders
    for (const prefixRef of listResult.prefixes) {
      const subfolder = await recursiveListStorage(prefixRef.fullPath);
      folder.subfolders.push(subfolder);
    }
  } catch (error) {
    console.error("Error listing storage path:", path, error);
  }

  return folder;
}

/**
 * Recursively add files and folders to zip preserving folder hierarchy.
 * @param {Object} folder - Folder object containing subfolders and files.
 * @param {string} basePath - The base path inside the zip for this folder.
 * @param {JSZip} zip - JSZip instance to add files to.
 */
async function addFolderToZip(folder, basePath, zip) {
  // Add files in this folder (if any)
  if (folder.files && folder.files.length > 0) {
    for (const file of folder.files) {
      try {
        const response = await fetch(file.downloadURL);
        const blob = await response.blob();
        zip.file(`${basePath}/${file.name}`, blob);
      } catch (error) {
        console.error("Error fetching file for download:", file.name, error);
      }
    }
  }

  // Recursively add subfolders
  if (folder.subfolders && folder.subfolders.length > 0) {
    for (const subfolder of folder.subfolders) {
      const subfolderPath = `${basePath}/${
        subfolder.folderName || "subfolder"
      }`;
      await addFolderToZip(subfolder, subfolderPath, zip);
    }
  }
}

/**
 * Download a folder as a zip archive preserving folder structure.
 * @param {Object} folder - Folder object with folderName, userId, folderId.
 * @param {string} userId - User ID.
 * @param {string} folderId - Folder ID.
 */
export async function downloadFolderAsZip(folder, userId, folderId) {
  if (!folder || !userId || !folderId) {
    throw new Error("Missing required parameters for downloadFolderAsZip");
  }

  const storagePath = `users/${userId}/folders/${folderId}`;
  const folderStructure = await recursiveListStorage(storagePath);

  const zip = new JSZip();
  const basePath = folder.folderName || "folder";

  await addFolderToZip(folderStructure, basePath, zip);

  try {
    const content = await zip.generateAsync({ type: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(content);
    link.download = `${basePath}.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error generating zip file:", error);
    throw error;
  }
}
